const LOCALIZATION_LINKS = [
  {
    localization: 'fr',
    link: 'https://www.swimmy.fr',
  },
  {
    localization: 'es',
    link: 'https://swimmy.es',
  },
];

const EXTRA_DOMAINS_BY_LOCALIZATION = {
  fr: ['www.swimmy.fr', 'swimmy.fr'],
  es: ['www.swimmy.es', 'swimmy.es'],
};

const BASE_LINK_RULES = [
  {
    id: 1,
    regex: '^(?!https?://).*',
    mode: 'external',
  },
  {
    id: 2,
    regex: 'https?://([-\\w]+\\.)*facebook\\.com.*',
    mode: 'appbrowser',
  },
  {
    id: 3,
    regex: 'https?://([\\-\\w]+\\.)*(twitter|x)\\.com/.*',
    mode: 'appbrowser',
  },
  {
    id: 4,
    regex: 'https?://([-\\w]+\\.)*instagram\\.com/.*',
    mode: 'appbrowser',
  },
  {
    id: 5,
    regex: 'https?://maps\\.google\\.com.*',
    mode: 'appbrowser',
  },
  {
    id: 6,
    regex: 'https?://([-\\w]+\\.)*google\\.com/maps/search/.*',
    mode: 'appbrowser',
  },
  {
    id: 7,
    regex: 'https?://([-\\w]+\\.)*linkedin\\.com/.*',
    mode: 'appbrowser',
  },
  {
    id: 8,
    regex: 'https?://login\\.microsoftonline\\.com.*',
    mode: 'internal',
  },
];

const FALLBACK_LINK_RULE = {
  id: 999,
  regex: '.*',
  mode: 'appbrowser',
};

const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const unique = (values) => Array.from(new Set(values));

const createDomainRegex = (domains) => {
  const escapedDomains = domains.map(escapeRegex).join('|');
  return `https?:\\/\\/(?:${escapedDomains})(?:\\/.*)?$`;
};

const createDomains = (localization, link) => {
  const hostname = new URL(link).hostname.toLowerCase();
  const extraDomains = EXTRA_DOMAINS_BY_LOCALIZATION[localization] || [];
  return unique([hostname, ...extraDomains]);
};

export const DEFAULT_MEDIAN_LOCALIZATION = LOCALIZATION_LINKS[0].localization;

export const medianLocalizationRules = LOCALIZATION_LINKS.map(({ localization, link }) => {
  const domains = createDomains(localization, link);

  return {
    localization,
    link,
    domains,
    domainRegex: createDomainRegex(domains),
  };
});

export const normalizeMedianLocalization = (localization) => {
  if (!localization) {
    return DEFAULT_MEDIAN_LOCALIZATION;
  }

  const lowerCasedLocalization = localization.toLowerCase();

  if (lowerCasedLocalization === 'fr-be') {
    return 'fr-BE';
  }

  const exactMatch = medianLocalizationRules.find(
    ({ localization: loc }) => loc.toLowerCase() === lowerCasedLocalization
  );

  if (exactMatch) {
    return exactMatch.localization;
  }

  const baseLocalization = lowerCasedLocalization.split('-')[0];
  const baseMatch = medianLocalizationRules.find(
    ({ localization: loc }) => loc.toLowerCase() === baseLocalization
  );

  return baseMatch ? baseMatch.localization : DEFAULT_MEDIAN_LOCALIZATION;
};

export const getMedianLocalizationRule = (localization) => {
  const normalizedLocalization = normalizeMedianLocalization(localization);
  return (
    medianLocalizationRules.find(({ localization: loc }) => loc === normalizedLocalization) ||
    medianLocalizationRules[0]
  );
};

export const getMedianLocalizationLink = (localization) => {
  const localizationRule = getMedianLocalizationRule(localization);
  return localizationRule.link;
};

export const getMedianLocalizationFromHostname = (hostname) => {
  if (!hostname) {
    return DEFAULT_MEDIAN_LOCALIZATION;
  }

  const normalizedHostname = hostname.toLowerCase().split(':')[0];
  const matchingRule = medianLocalizationRules.find(({ domains }) =>
    domains.some((domain) => domain === normalizedHostname)
  );

  return matchingRule ? matchingRule.localization : DEFAULT_MEDIAN_LOCALIZATION;
};

const getOrderedLocalizationRules = (localization) => {
  const normalizedLocalization = normalizeMedianLocalization(localization);

  return [...medianLocalizationRules].sort((a, b) => {
    if (a.localization === normalizedLocalization) {
      return -1;
    }

    if (b.localization === normalizedLocalization) {
      return 1;
    }

    return 0;
  });
};

export const getMedianInternalExternalRules = (localization) => {
  const orderedLocalizationRules = getOrderedLocalizationRules(localization);

  const localizationRules = orderedLocalizationRules.map((rule, index) => ({
    id: 100 + index,
    regex: rule.domainRegex,
    mode: 'internal',
  }));

  return [...BASE_LINK_RULES, ...localizationRules, FALLBACK_LINK_RULE];
};

const LEVEL_1_ROUTES = [
  'LandingPage',
  'SearchPage',
  'SearchFiltersPage',
  'SearchListingsPage',
  'SearchMapPage',
  'ManageListingsPage',
  'ReservationsBasePage',
  'ReservationsPage',
  'AccountSettingsPage',
];

const LEVEL_2_ROUTES = [
  'NewListingPage',
  'EditListingPage',
  'OrderPage',
  'OrderDetailsPage',
  'SalePage',
  'SaleDetailsPage',
  'IncomePage',
  'AvailabilitiesPage',
  'ProfilePage',
  'ListingPage',
  'ListingPageCanonical',
  'CheckoutPage',
  'CheckoutProposalPage',
  'CheckoutExtraPage',
  'ContactPage',
  'ListingPageVariant',
  'CheckoutPageMultiBooking',
  'AccountSecurityPage',
  'AccountPasswordChangePage',
  'AccountProfilePage',
  'AccountPersonalInfosPage',
  'AccountPaymentsPage',
  'AccountPaymentMethodsPage',
  'AccountPayoutDetailsPage',
  'AccountPrivacyPage',
  'CongratsEnquiryPage',
  'CongratsProposalPage',
  'CongratPoolPage',
  'CalendarPage',
  'SwimmingEnthusiastsArticlePage',
  'CleaningPoolArticlePage',
  'SimulatorStandalonePage',
];

const hasValue = (value) => typeof value === 'string' && value.length > 0;

const ROUTE_LEVEL_BY_NAME = Object.fromEntries([
  ...LEVEL_1_ROUTES.map((routeName) => [routeName, 1]),
  ...LEVEL_2_ROUTES.map((routeName) => [routeName, 2]),
]);

export const getMedianNavigationLevelMatch = (routeContext = {}) => {
  const { routeName, routeParams = {} } = routeContext;

  if (!routeName) {
    return null;
  }

  if (routeName === 'InboxPage') {
    return {
      level: hasValue(routeParams.id) ? 2 : 1,
      routeName,
      routeParams,
    };
  }

  if (routeName === 'LocationsPage') {
    return {
      level: hasValue(routeParams.slug) ? 2 : null,
      routeName,
      routeParams,
    };
  }

  const level = ROUTE_LEVEL_BY_NAME[routeName];

  return level ? { level, routeName, routeParams } : null;
};

export const getMedianNavigationLevelFromRoute = (routeContext = {}) => {
  const matchingRule = getMedianNavigationLevelMatch(routeContext);

  return matchingRule?.level || null;
};

const NAVIGATION_LEVEL_PATH_RULES = [
  { routeName: 'LandingPage', level: 1, pathRegex: '^/$' },
  { routeName: 'SearchPage', level: 1, pathRegex: '^/s$' },
  { routeName: 'SearchFiltersPage', level: 1, pathRegex: '^/s/filters$' },
  { routeName: 'SearchListingsPage', level: 1, pathRegex: '^/s/listings$' },
  { routeName: 'SearchMapPage', level: 1, pathRegex: '^/s/map$' },
  { routeName: 'ManageListingsPage', level: 1, pathRegex: '^/listings$' },
  { routeName: 'ReservationsBasePage', level: 1, pathRegex: '^/reservations$' },
  { routeName: 'ReservationsPage', level: 1, pathRegex: '^/reservations/[^/]+$' },
  { routeName: 'AccountSettingsPage', level: 1, pathRegex: '^/account-settings$' },
  { routeName: 'InboxPage', level: 1, pathRegex: '^/messages$' },
  { routeName: 'InboxPage', level: 2, pathRegex: '^/messages/[^/]+$' },
  { routeName: 'NewListingPage', level: 2, pathRegex: '^/l/new$' },
  {
    routeName: 'EditListingPage',
    level: 2,
    pathRegex: '^/l/[^/]+/[0-9a-fA-F-]{36}/(new|draft|edit)/[^/]+$',
  },
  { routeName: 'IncomePage', level: 2, pathRegex: '^/income$' },
  { routeName: 'AvailabilitiesPage', level: 2, pathRegex: '^/your-availabilities$' },
  {
    routeName: 'CongratPoolPage',
    level: 2,
    pathRegex: '^/congratulation-after-ad-creation$',
  },
  { routeName: 'OrderPage', level: 2, pathRegex: '^/order/[^/]+$' },
  { routeName: 'OrderDetailsPage', level: 2, pathRegex: '^/order/[^/]+/details$' },
  { routeName: 'SalePage', level: 2, pathRegex: '^/sale/[^/]+$' },
  { routeName: 'SaleDetailsPage', level: 2, pathRegex: '^/sale/[^/]+/details$' },
  { routeName: 'ProfilePage', level: 2, pathRegex: '^/u/.+$' },
  { routeName: 'ListingPage', level: 2, pathRegex: '^/l/[^/]+/[0-9a-fA-F-]{36}$' },
  { routeName: 'ListingPageCanonical', level: 2, pathRegex: '^/l/[0-9a-fA-F-]{36}$' },
  { routeName: 'CheckoutPage', level: 2, pathRegex: '^/l/[^/]+/[0-9a-fA-F-]{36}/checkout$' },
  {
    routeName: 'CheckoutProposalPage',
    level: 2,
    pathRegex: '^/l/[^/]+/[0-9a-fA-F-]{36}/checkout/proposal$',
  },
  {
    routeName: 'CheckoutExtraPage',
    level: 2,
    pathRegex: '^/l/[^/]+/[0-9a-fA-F-]{36}/checkout/extra$',
  },
  { routeName: 'ContactPage', level: 2, pathRegex: '^/l/[^/]+/[0-9a-fA-F-]{36}/contact$' },
  { routeName: 'ListingPageVariant', level: 2, pathRegex: '^/l/[^/]+/[0-9a-fA-F-]{36}/.+$' },
  { routeName: 'CheckoutPageMultiBooking', level: 2, pathRegex: '^/cart/checkout$' },
  { routeName: 'AccountSecurityPage', level: 2, pathRegex: '^/account-settings/security$' },
  {
    routeName: 'AccountPasswordChangePage',
    level: 2,
    pathRegex: '^/account-settings/security/change-password$',
  },
  { routeName: 'AccountProfilePage', level: 2, pathRegex: '^/account-settings/profile$' },
  {
    routeName: 'AccountPersonalInfosPage',
    level: 2,
    pathRegex: '^/account-settings/personal-infos$',
  },
  { routeName: 'AccountPaymentsPage', level: 2, pathRegex: '^/account-settings/payments$' },
  {
    routeName: 'AccountPaymentMethodsPage',
    level: 2,
    pathRegex: '^/account-settings/payments/payment-methods$',
  },
  {
    routeName: 'AccountPayoutDetailsPage',
    level: 2,
    pathRegex: '^/account-settings/payments/payout-details$',
  },
  { routeName: 'AccountPrivacyPage', level: 2, pathRegex: '^/account-settings/privacy$' },
  { routeName: 'CongratsEnquiryPage', level: 2, pathRegex: '^/enquiry/[^/]+/congrats$' },
  { routeName: 'CongratsProposalPage', level: 2, pathRegex: '^/proposal/[^/]+/congrats$' },
  { routeName: 'CalendarPage', level: 2, pathRegex: '^/calendar/[^/]+$' },
  {
    routeName: 'SwimmingEnthusiastsArticlePage',
    level: 2,
    pathRegex: '^/amateurs-de-baignade/[^/]+$',
  },
  {
    routeName: 'CleaningPoolArticlePage',
    level: 2,
    pathRegex: '^/entretien-piscine/[^/]+$',
  },
  { routeName: 'LocationsPage', level: 2, pathRegex: '^/locations/[^/]+$' },
  { routeName: 'SimulatorStandalonePage', level: 2, pathRegex: '^/simulator/standalone$' },
];

const withoutAnchors = (regex) => regex.replace(/^\^/, '').replace(/\$$/, '');

const createFullUrlRegex = (domains, pathRegex) => {
  const escapedDomains = domains.map(escapeRegex).join('|');
  return `^https?:\\/\\/(?:${escapedDomains})${withoutAnchors(pathRegex)}(?:[?#].*)?$`;
};

const getAllMedianDomains = () => unique(medianLocalizationRules.flatMap(({ domains }) => domains));

export const getMedianNavigationLevelsData = (hostname) => {
  const domains = unique([
    ...getAllMedianDomains(),
    ...(hostname ? [hostname.toLowerCase().split(':')[0]] : []),
  ]);

  const levels = NAVIGATION_LEVEL_PATH_RULES.map(({ level, pathRegex }) => ({
    regex: createFullUrlRegex(domains, pathRegex),
    level,
  }));

  return {
    active: true,
    levels,
  };
};
